import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Gift, Download, CheckCircle, XCircle, Clock } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const OCCASION_LABELS = {
  birthday: "Bursdag",
  christmas: "Jul",
  easter: "Påske",
  mothersday: "Morsdag",
  womensday: "Kvinnedag",
  occasion: "Annen Anledning",
  none: "Ingen Anledning",
};

const AdminGiftCardPage = () => {
  const [searchCode, setSearchCode] = useState("");
  const [cardDetails, setCardDetails] = useState(null);
  const [allCards, setAllCards] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const handleSearch = async () => {
    if (!searchCode.trim()) {
      toast.error("Skriv inn en gavekort-kode");
      return;
    }
    setSearching(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/gift-cards/${searchCode.trim()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCardDetails(response.data);
      toast.success("Gavekort funnet!");
    } catch (error) {
      setCardDetails(null);
      toast.error(error.response?.data?.detail || "Gavekort ikke funnet");
    } finally {
      setSearching(false);
    }
  };

  const handleLoadAll = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/gift-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllCards(response.data);
      setShowAll(true);
      toast.success(`Fant ${response.data.length} gavekort`);
    } catch (error) {
      toast.error("Kunne ikke laste gavekort");
    }
  };

  const handleDownloadPDF = (code) => {
    window.open(`${API}/gift-cards/${code}/pdf`, "_blank");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("nb-NO");
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="min-h-screen" style={{ background: "#f4e7d8" }}>
      <Navbar />

      <div
        className="py-12 mandala-pattern"
        style={{ background: "linear-gradient(135deg, #721111 0%, #2d1810 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-serif-display font-semibold italic"
            style={{ color: "#d4af37" }}
          >
            Gavekort Administrasjon
          </motion.h1>
        </div>
      </div>

      <div className="h-2" style={{ background: "linear-gradient(90deg, #721111, #d4af37, #721111)" }}></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg p-6" style={{ border: "2px solid #d4af37" }}>
          <h2 className="text-xl font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>
            Sjekk Gavekort
          </h2>
          <div className="flex gap-3">
            <Input
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              placeholder="Skriv inn gavekort-kode (f.eks. GC-ABC123DEF456)"
              className="indian-input flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              data-testid="giftcard-search-input"
            />
            <Button onClick={handleSearch} disabled={searching} className="btn-maroon rounded-full px-6" data-testid="giftcard-search-button">
              <Search className="h-4 w-4 mr-2" />
              {searching ? "Søker..." : "Søk"}
            </Button>
          </div>
        </div>

        {/* Card Details */}
        {cardDetails && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
            style={{ border: "2px solid #b27f47" }}
            data-testid="giftcard-details-card"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold" style={{ color: "#721111" }}>{cardDetails.card_code}</h3>
                <div className="flex items-center gap-2 mt-1">
                  {cardDetails.payment_status === "paid" ? (
                    <span className="flex items-center gap-1 text-sm" style={{ color: "#006400" }}>
                      <CheckCircle className="h-4 w-4" /> Betalt
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm" style={{ color: "#dc2626" }}>
                      <XCircle className="h-4 w-4" /> {cardDetails.payment_status}
                    </span>
                  )}
                  {isExpired(cardDetails.expires_at) && (
                    <span className="flex items-center gap-1 text-sm" style={{ color: "#dc2626" }}>
                      <Clock className="h-4 w-4" /> Utløpt
                    </span>
                  )}
                </div>
              </div>
              <Button onClick={() => handleDownloadPDF(cardDetails.card_code)} className="btn-maroon rounded-full" data-testid="download-giftcard-pdf">
                <Download className="h-4 w-4 mr-2" /> Last ned PDF
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 rounded-xl" style={{ background: "#f4e7d8" }}>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Opprinnelig Beløp</Label>
                <p className="font-bold text-lg" style={{ color: "#2d1810" }}>kr {cardDetails.initial_balance},-</p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Gjenstående Saldo</Label>
                <p className="font-bold text-lg" style={{ color: cardDetails.current_balance > 0 ? "#006400" : "#dc2626" }}>
                  kr {cardDetails.current_balance},-
                </p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Anledning</Label>
                <p className="font-semibold" style={{ color: "#2d1810" }}>{OCCASION_LABELS[cardDetails.occasion] || cardDetails.occasion || "Ingen"}</p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Fra</Label>
                <p className="font-semibold" style={{ color: "#2d1810" }}>{cardDetails.sender_name || "—"}</p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Til</Label>
                <p className="font-semibold" style={{ color: "#2d1810" }}>{cardDetails.recipient_name || "—"}</p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Kjøper E-post</Label>
                <p className="font-semibold text-sm" style={{ color: "#2d1810" }}>{cardDetails.purchaser_email}</p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Opprettet</Label>
                <p className="font-semibold" style={{ color: "#2d1810" }}>{formatDate(cardDetails.created_at)}</p>
              </div>
              <div>
                <Label className="text-xs" style={{ color: "#8b4513" }}>Gyldig Til</Label>
                <p className="font-semibold" style={{ color: isExpired(cardDetails.expires_at) ? "#dc2626" : "#2d1810" }}>
                  {formatDate(cardDetails.expires_at)}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* All Cards */}
        <div className="bg-white rounded-xl shadow-lg p-6" style={{ border: "2px solid #b27f47" }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-serif-display font-semibold" style={{ color: "#2d1810" }}>
              Alle Gavekort
            </h2>
            <Button onClick={handleLoadAll} className="btn-maroon rounded-full" data-testid="load-all-giftcards-button">
              <Gift className="h-4 w-4 mr-2" /> Vis Alle
            </Button>
          </div>

          {showAll && allCards.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "2px solid #d4af37" }}>
                    <th className="text-left py-2 px-2" style={{ color: "#2d1810" }}>Kode</th>
                    <th className="text-left py-2 px-2" style={{ color: "#2d1810" }}>Beløp</th>
                    <th className="text-left py-2 px-2" style={{ color: "#2d1810" }}>Saldo</th>
                    <th className="text-left py-2 px-2" style={{ color: "#2d1810" }}>Fra / Til</th>
                    <th className="text-left py-2 px-2" style={{ color: "#2d1810" }}>Status</th>
                    <th className="text-left py-2 px-2" style={{ color: "#2d1810" }}>Gyldig Til</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {allCards.map((card) => (
                    <tr key={card.card_code} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td className="py-2 px-2 font-mono text-xs font-bold" style={{ color: "#721111" }}>{card.card_code}</td>
                      <td className="py-2 px-2" style={{ color: "#2d1810" }}>kr {card.initial_balance},-</td>
                      <td className="py-2 px-2 font-semibold" style={{ color: card.current_balance > 0 ? "#006400" : "#dc2626" }}>
                        kr {card.current_balance},-
                      </td>
                      <td className="py-2 px-2 text-xs" style={{ color: "#4a3728" }}>
                        {card.sender_name || "—"} → {card.recipient_name || "—"}
                      </td>
                      <td className="py-2 px-2">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-semibold"
                          style={{
                            background: card.payment_status === "paid" ? "#dcfce7" : "#fef9c3",
                            color: card.payment_status === "paid" ? "#166534" : "#854d0e",
                          }}
                        >
                          {card.payment_status === "paid" ? "Betalt" : card.payment_status}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-xs" style={{ color: isExpired(card.expires_at) ? "#dc2626" : "#4a3728" }}>
                        {formatDate(card.expires_at)}
                      </td>
                      <td className="py-2 px-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadPDF(card.card_code)}
                          style={{ color: "#721111" }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showAll && allCards.length === 0 && (
            <p className="text-center py-8" style={{ color: "#4a3728" }}>Ingen gavekort funnet</p>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminGiftCardPage;
